import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import classnames from 'classnames';

import { isNotEmpty, scrollToAnchor, doScrollToErrorMessage } from '~/utils';
import {
  appointmentDraftActions,
  appointmentDraftSelectors,
  journeyStateActions,
  shopsApi,
  shopsSelectors,
} from '~/api';
import {
  loadInitialSlots,
  loadPreviousSlots,
  loadNextSlots,
  setSlots,
  replaceFilters,
  createQueryParams,
} from '~/routes/select-date/select-date.actions';
import { loadPartners } from '~/routes/partners/partners.actions';
import {
  CalendarWithFilters,
  eligibleSlotsFilter as defaultEligibleSlotsFilter,
  errorMessagesActions,
  errorMessages,
  errorTypes,
  Messages,
  scrollToMessageTypeAnchor,
  apiCallFinished,
  StateContext,
  states,
  withRestorableDraft,
  calendarSelectors,
  warningModalSelectors,
  TitleWithSubtitle,
  envConfigSelectors,
} from '~/components';
import {
  saveTemporaryAppointment,
  setEditMode,
  replaceTimeFilters,
  saveTemporaryJourneyState,
} from '../../edit-appointment.actions';
import { triggerEditPageSaveEditsAnalytics } from '~/analytics/analytics.listener';

import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';
import { formatToTemporaryState } from '../../helpers';

import {
  getTokenFromStorage,
  removeTokenFromStorage,
  TEMPORARY_TOKEN_NAME,
} from '~/components/persist-token/persist-token.utils';

import styles from '~/routes/select-date/SelectDate.scss';
import editStyles from './EditDate.scss';
import { setHeadlineBackButtonConfig } from '~/ocab-services/services.actions';

import { selectDateSelectors } from '~/routes/select-date/select-date.selectors';
import { calendarHelpers } from '~/components/calendar-with-filters/helpers/format-slots-data';
import { formatInHoursAndMinutes, msToMinutes, dateUtils } from '~/routes/utils';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ children }) => children);

const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR, NO_AVAILABLE_PARTNERS_ERROR } =
  errorTypes;

const getEligibleSlotsFilter = (config) =>
  config.Calendar && config.Calendar.eligibleSlotsFilter
    ? config.Calendar.eligibleSlotsFilter
    : defaultEligibleSlotsFilter;

export class EditDate extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  constructor(props) {
    super(props);

    const { dispatchSetSlots } = props;

    dispatchSetSlots([]);
  }

  componentDidMount() {
    const {
      mainAppointmentDraft,
      temporaryAppointment,
      ChosenPartner,

      dispatchLoadPartners,
      dispatchApiCallFinished,
    } = this.props;

    dispatchApiCallFinished(false);
    this.saveOriginalData();

    if (ChosenPartner) {
      dispatchLoadPartners();
    }
    if (isNotEmpty(temporaryAppointment)) {
      const { partner } = mainAppointmentDraft;

      this.checkSlotAvailability(partner.id)
        .then(this.resolveSlots)
        .catch(this.handleAvailabilityError);
    }

    window.scrollTo(0, 0);
  }

  componentDidUpdate({ mainAppointmentDraft: prevMainAppointmentDraft }) {
    const {
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,
      slotErrorMessages,

      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && mainAppointmentDraft.dateSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (
      slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] &&
      mainAppointmentDraft.dateSlot !== prevMainAppointmentDraft.dateSlot
    ) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && mainAppointmentDraft.timeSlot.from) {
      if (!isMultipleAppointment && mainAppointmentDraft.timeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      } else if (mainAppointmentDraft.timeSlot.from && relatedAppointmentTimeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      }
    }

    doScrollToErrorMessage(!mainAppointmentDraft.timeSlot.from); // Validate condition to scroll to error message

    if (prevMainAppointmentDraft.dateSlot === '' && mainAppointmentDraft.dateSlot) {
      scrollToAnchor('time-slot-title');
    }
  }

  saveOriginalData = () => {
    const { filters, originalFilters, dispatchReplaceTimeFilters } = this.props;

    if (!originalFilters.length) {
      dispatchReplaceTimeFilters(filters);
    }
  };

  checkSlotAvailability = (partnerId) => {
    const { mainAppointmentDraft, relatedAppointmentTimeSlot, relatedAppointmentTypeId } =
      this.props;

    if (!mainAppointmentDraft.dateSlot) {
      return Promise.resolve({
        isDateSlotAvailable: true,
        isTimeSlotAvailable: true,
      });
    }

    const requestParams = createQueryParams(
      {
        ...mainAppointmentDraft,
        partner: {
          ...mainAppointmentDraft.partner,
          id: partnerId,
        },
      },
      mainAppointmentDraft.dateSlot,
    );

    const {
      dateSlot,
      timeSlot,
      shop: { id: shopId },
      appointmentType: { id: mainAppointmentTypeId },
    } = mainAppointmentDraft;

    return shopsApi
      .fetch(`/${shopId}/slots?${requestParams}`, {}, shopId ? [404] : [])
      .then(({ slots }) => {
        const newDateSlot = calendarHelpers.findDateSlot(slots, dateSlot);
        const newTimeSlot = calendarHelpers.findTimeSlot(
          newDateSlot,
          timeSlot,
          mainAppointmentTypeId,
        );
        const newRelatedAppointmentTimeSlot =
          relatedAppointmentTimeSlot &&
          calendarHelpers.findTimeSlot(
            newDateSlot,
            relatedAppointmentTimeSlot,
            relatedAppointmentTypeId,
          );

        return {
          isDateSlotAvailable: !!newDateSlot && newDateSlot.times.length > 0,
          mainAppointmentTimeSlot: newTimeSlot,
          relatedAppointmentTimeSlot: newRelatedAppointmentTimeSlot,
        };
      });
  };

  resolveSlots = ({ isDateSlotAvailable, mainAppointmentTimeSlot, relatedAppointmentTimeSlot }) => {
    const {
      relatedAppointmentTypeId,
      isMultipleAppointment,

      dispatchSelectDateSlot,
      dispatchSelectTimeSlot,
      dispatchSetRelatedAppointmentTimeSlot,
    } = this.props;

    if (!isDateSlotAvailable) {
      dispatchSelectDateSlot();
    } else if (!mainAppointmentTimeSlot) {
      dispatchSelectTimeSlot();
    }

    if (mainAppointmentTimeSlot) {
      dispatchSelectTimeSlot(mainAppointmentTimeSlot);
    }

    if (isMultipleAppointment) {
      if (!relatedAppointmentTimeSlot) {
        dispatchSetRelatedAppointmentTimeSlot({ appointmentTypeId: relatedAppointmentTypeId });
      }

      if (relatedAppointmentTimeSlot) {
        dispatchSetRelatedAppointmentTimeSlot(relatedAppointmentTimeSlot);
      }
    }

    this.loadSlots();
  };

  handleAvailabilityError = ({ status }) => {
    const {
      mainAppointmentDraft: { shop },
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    dispatchApiCallFinished(true);

    if (status === 404) {
      const message = errorMessages[NO_AVAILABLE_PARTNERS_ERROR];
      const actionMessage = {
        type: NO_AVAILABLE_PARTNERS_ERROR,
        title: message.createTitle(shop.name),
      };

      dispatchResetErrorMessagesWith([actionMessage]);
    }
  };

  selectPartnersGroup = (group) => {
    const {
      dispatchSelectPartnersGroup,
      dispatchSelectDateSlot,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
      dispatchSetSlots,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectDateSlot();
    dispatchSetSlots([]);
    dispatchSelectPartnersGroup(group.id);
    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(this.loadSlots);
  };

  selectPartner = (partner) => {
    const { dispatchSelectPartner, dispatchSetSlots, dispatchApiCallFinished } = this.props;

    if (partner !== null) {
      dispatchSelectPartner(partner);

      // do not remove setTimeout it's one of the AC in BKM-227
      window.setTimeout(() => {
        dispatchApiCallFinished(false);
        dispatchSetSlots([]);

        this.checkSlotAvailability(partner.id).then(this.resolveSlots);
      }, 500);
      scrollToAnchor('chosen-partner');
    }
  };

  loadSlotsSuccessHandler = (date) => {
    const {
      dispatchLoadPreviousSlots,
      dispatchLoadNextSlots,
      mainAppointmentDraft: {
        appointmentType: { maxAdvance },
      },
      config,
    } = this.props;

    const previousStartDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(date, -1);
    const nextStartDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(date, 1);

    if (dateUtils.isDateInCalendarRange(previousStartDate, maxAdvance)) {
      dispatchLoadPreviousSlots(previousStartDate, getEligibleSlotsFilter(config));
    }
    if (dateUtils.isDateInCalendarRange(nextStartDate, maxAdvance)) {
      dispatchLoadNextSlots(nextStartDate, getEligibleSlotsFilter(config));
    }
  };

  loadSlots = () => {
    const {
      dispatchLoadInitialSlots,
      dispatchApiCallFinished,
      mainAppointmentDraft: { dateSlot: date },
      config,
    } = this.props;

    const startDate = dateUtils.getSlotsStartDate(date);

    const monthCountToDownload = date ? 1 : 2;

    dispatchLoadInitialSlots(
      startDate,
      monthCountToDownload,
      getEligibleSlotsFilter(config),
      getTokenFromStorage(TEMPORARY_TOKEN_NAME),
    ).then(() => {
      dispatchApiCallFinished(true);
      this.setState({
        isAjaxCompleted: true,
      });
      if (date) {
        this.loadSlotsSuccessHandler(startDate);
      }
    });
  };

  saveHandler = () => {
    const {
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,

      onSaveDataHandler,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    if (!mainAppointmentDraft.dateSlot) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (!mainAppointmentDraft.timeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else if (isMultipleAppointment && !relatedAppointmentTimeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      return onSaveDataHandler()
        .then(() => dispatchApiCallFinished(true))
        .then(() => this.navigateToReviewPage())
        .then(() => triggerEditPageSaveEditsAnalytics('appointments'))
        .catch(() => {
          this.exitEditMode();
        });
    }
    return null;
  };

  selectShop = (shop) => {
    const {
      dispatchApiCallFinished,
      dispatchSelectPartner,
      dispatchSelectDateSlot,
      dispatchSelectShop,
      dispatchUpdateAppointmentType,
      dispatchLoadPartners,
      dispatchSetSlots,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchSelectPartner();
    dispatchSelectDateSlot();
    dispatchSetSlots([]);
    dispatchSelectShop(shop);

    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(this.loadSlots);
  };

  navigateToReviewPage = () => {
    const { history } = this.props;
    const {
      location: { pathname },
    } = history;
    this.exitEditMode();
    const match = matchPath(pathname, { path: '/:appointmentId([0-9]+)' }) || { params: {} };
    history.pushWithToken(
      match.params.appointmentId ? `/${match.params.appointmentId}` : '/review',
    );
  };

  exitEditMode = () => {
    const {
      dispatchSetEditMode,
      dispatchSaveTemporaryAppointment,
      dispatchReplaceTimeFilters,
      dispatchSaveTemporaryJourneyState,
    } = this.props;
    dispatchSetEditMode(false);
    dispatchSaveTemporaryAppointment({});
    dispatchSaveTemporaryJourneyState({});
    dispatchReplaceTimeFilters([]);
    removeTokenFromStorage(TEMPORARY_TOKEN_NAME);
  };

  onCancelHandler = () => {
    const { originalFilters, dispatchReplaceFilters, dispatchReplaceTimeFilters } = this.props;

    dispatchReplaceTimeFilters([]);
    dispatchReplaceFilters(originalFilters);
  };

  render() {
    const {
      mainAppointmentDraft,
      journeyState,
      filteredDateSlots,
      partners,
      dispatchLoadPreviousSlots,
      dispatchLoadNextSlots,
      ChosenPartner,
      GroupsDropdown,
      config: { Calendar = {}, partnerTitle, ...restConfig },
      temporaryAppointment,
      history,
      dispatchSetEditMode,
      dispatchSetDraftAppointment,
      dispatchSaveTemporaryAppointment,
      prevPath,
      editMode,
      temporaryJourneyState,
      dispatchSetJourneyState,
      location,
      showWarningModal,
      title,
      subtitleComponent: Subtitle,
      ShopsDropdown,
      shopList,
      isRemoteConfigEnabled,
      dispatchSetHeadlineBackButtonConfig,
      hideGoBackButton,
      inStoreAppointmentTypeId,
      isMultipleAppointment,
      isApiCallFinished,
    } = this.props;

    const {
      calendarTimeSlotsIntro: CalendarTimeSlotsIntro,
      timeSlotsList,
      eligibleSlotsFilter = defaultEligibleSlotsFilter,
      formatTimeSlots,
    } = Calendar;
    const { isAjaxCompleted } = this.state;

    const {
      appointmentType: { groups: partnersGroups = [], maxAdvance, duration },
      partner: selectedPartner,
      shop: { name: selectedShopName, id: selectedShopId },
      id: bookingId,
      partnersGroupId: selectedPartnersGroupId,
    } = mainAppointmentDraft;
    const partnersGroupName = (
      partnersGroups.find((partnersGroup) => partnersGroup.id === selectedPartnersGroupId) || {}
    ).name;

    return (
      <section data-testid="EditDate" data-test-ajax={isAjaxCompleted}>
        <div className={editStyles.EditDateTitlesContainer}>
          <ScrollToMessageTypeAnchor forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}>
            <TitleWithSubtitle
              title={title}
              className={`${styles.Title} ${editStyles.EditTitleWithSubtitle}`}
              dataTest="EditDateTitle"
            ></TitleWithSubtitle>
          </ScrollToMessageTypeAnchor>
        </div>
        {Subtitle && (
          <Subtitle
            className={editStyles.Subtitle}
            classNameForSubtitleWithLink={styles.SubtitleWithLink}
            secondLineClassName={styles.Subtitle__second}
            inStoreAppointmentTypeId={inStoreAppointmentTypeId}
            hideInStoreLink
          />
        )}
        <div className={classnames({ [styles.Row]: isRemoteConfigEnabled || GroupsDropdown })}>
          {ShopsDropdown && !!shopList.length && (
            <ShopsDropdown
              selectedShopId={selectedShopId}
              shopList={shopList}
              onChange={this.selectShop}
              className={styles.Col}
            />
          )}
          {GroupsDropdown && (
            <GroupsDropdown
              partnersGroups={partnersGroups}
              selectedPartnersGroupId={selectedPartnersGroupId}
              onChange={this.selectPartnersGroup}
              className={styles.Col}
            />
          )}
          {!isMultipleAppointment && ChosenPartner && (
            <ChosenPartner
              partner={selectedPartner}
              partners={partners}
              selectPartner={this.selectPartner}
              selectedShopId={selectedShopId}
              isAppointmentTypeRemote={isRemoteConfigEnabled}
              isSeries={isRemoteConfigEnabled || !!GroupsDropdown}
              partnerTitle={partnersGroupName || partnerTitle}
              {...restConfig}
            />
          )}
        </div>
        <div className={styles.TitleAndCalendarWithFiltersContainer}>
          <CalendarWithFilters
            selectedDateSlot={filteredDateSlots}
            timeSlotsList={timeSlotsList}
            isFiltersHidden={!!GroupsDropdown}
            loadNextSlots={dispatchLoadNextSlots}
            loadPreviousSlots={dispatchLoadPreviousSlots}
            maxAdvance={maxAdvance}
            eligibleSlotsFilter={eligibleSlotsFilter}
            selectedPartner={selectedPartner}
            editMode={editMode}
            formatTimeSlots={formatTimeSlots}
            isRemoteConfigEnabled={isRemoteConfigEnabled}
            isApiCallFinished={isApiCallFinished}
            extraClassName="SelectDateBordered"
          >
            <div className={styles.ErrorMessageContainer}>
              <Messages forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]} />
            </div>

            <Messages forTypes={[SLOT_TIME_NOT_SELECTED_ERROR]} />

            {CalendarTimeSlotsIntro && (
              <CalendarTimeSlotsIntro
                shopName={selectedShopName}
                duration={formatInHoursAndMinutes(msToMinutes(duration))}
              />
            )}
          </CalendarWithFilters>
        </div>
        <StateContext.Provider value={states.edit}>
          <EditButtonsWrapper
            temporaryDraftAppointment={temporaryAppointment}
            history={history}
            location={location}
            showWarningModal={showWarningModal}
            bookingId={bookingId}
            state={formatToTemporaryState(mainAppointmentDraft)}
            journeyState={journeyState}
            saveHandler={this.saveHandler}
            setEditMode={dispatchSetEditMode}
            saveTemporaryState={dispatchSaveTemporaryAppointment}
            onCancelHandler={this.onCancelHandler}
            saveButtonText="Save"
            prevPath={prevPath}
            temporaryJourneyState={temporaryJourneyState}
            restoreAppointmentDraft={dispatchSetDraftAppointment}
            restoreJourneyState={dispatchSetJourneyState}
            hideGoBackButton={hideGoBackButton}
            setHeadlineBackButtonConfig={dispatchSetHeadlineBackButtonConfig}
          />
        </StateContext.Provider>
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    journeyState,
    loader: { isCallFinished },
    partners: { partners },
    services: { config },
    editAppointment: { temporaryAppointment, editMode, timeFilters, temporaryJourneyState },
    messages: {
      app: { errorMessages: slotErrorMessages },
    },
    router: { location },
    envConfig,
  } = state;

  const mainAppointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);

  const {
    appointmentType: { id: mainAppointmentTypeId },
  } = mainAppointmentDraft;

  const relatedAppointmentDraft = appointmentDraftSelectors.selectRelatedAppointmentDraftById()(
    state,
  ) || { appointmentType: {} };

  const selectedSlotErrorMessages = calendarSelectors.selectSlotMessages(slotErrorMessages);
  const warningModalState = warningModalSelectors.selectState(state);
  const remoteConfig = envConfigSelectors.remoteConfig(mainAppointmentTypeId)(state);
  const { inStoreAppointmentTypeId } = envConfigSelectors.selectAppointmentTypeConfig(
    envConfig,
    mainAppointmentTypeId,
  );

  return {
    mainAppointmentDraft,
    relatedAppointmentTimeSlot: relatedAppointmentDraft.timeSlot,
    relatedAppointmentTypeId: relatedAppointmentDraft.appointmentType.id,

    journeyState,
    filteredDateSlots: selectDateSelectors.selectDateSlot(mainAppointmentTypeId)(state),
    filters: selectDateSelectors.selectFilters(state),
    originalFilters: timeFilters,
    partners,
    config,
    temporaryAppointment,
    temporaryJourneyState,
    editMode,
    prevPath: remoteConfig.editDatePrevPath || config.editDatePrevPath,
    slotErrorMessages: selectedSlotErrorMessages,
    location,
    showWarningModal: !!warningModalState.isOpen,
    isRemoteConfigEnabled: isNotEmpty(remoteConfig),
    title:
      envConfigSelectors.selectTitleForSelectDate(mainAppointmentTypeId)(state) ||
      'Choose a date for your appointment',
    subtitleComponent: envConfigSelectors.selectSubtitleComponent(mainAppointmentTypeId)(state),
    ShopsDropdown: remoteConfig.ShopsDropdown,
    shopList: shopsSelectors.selectShopList(state),
    hideEditDateTitle: envConfigSelectors.hideEditDateTitle(mainAppointmentTypeId)(state),
    hideGoBackButton: envConfigSelectors.hideEditDateGoBackButton(mainAppointmentTypeId)(state),
    inStoreAppointmentTypeId,
    ChosenPartner: envConfigSelectors.selectChosenPartner(mainAppointmentTypeId)(state),
    GroupsDropdown: envConfigSelectors.selectGroupsDropdown(state),
    isMultipleAppointment: appointmentDraftSelectors.isMultipleAppointment(state),
    isApiCallFinished: isCallFinished,
  };
};

const mapDispatchToProps = {
  dispatchLoadInitialSlots: loadInitialSlots,
  dispatchLoadPreviousSlots: loadPreviousSlots,
  dispatchLoadNextSlots: loadNextSlots,
  dispatchLoadPartners: loadPartners,
  dispatchSetSlots: setSlots,

  dispatchSelectDateSlot: appointmentDraftActions.setDateSlot,
  dispatchSelectTimeSlot: appointmentDraftActions.setTimeSlot,
  dispatchSetRelatedAppointmentTimeSlot: appointmentDraftActions.setRelatedAppointmentTimeSlot,

  dispatchSelectPartner: appointmentDraftActions.setPartner,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchApiCallFinished: apiCallFinished,
  dispatchSetEditMode: setEditMode,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchReplaceTimeFilters: replaceTimeFilters,
  dispatchReplaceFilters: replaceFilters,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
  dispatchSaveTemporaryJourneyState: saveTemporaryJourneyState,
  dispatchSetHeadlineBackButtonConfig: setHeadlineBackButtonConfig,
  dispatchSelectPartnersGroup: appointmentDraftActions.setPartnersGroupId,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
};

export const ConnectedEditDate = withRestorableDraft(
  connect(mapStateToProps, mapDispatchToProps)(EditDate),
);

export const ConnectedEditDateForManageAppointment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditDate);

const shopModel = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

const dateSlotModel = {
  date: PropTypes.string.isRequired,
  times: PropTypes.arrayOf(PropTypes.shape(timeSlotModel)),
};

EditDate.propTypes = {
  prevPath: PropTypes.string,
  partners: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  filters: PropTypes.arrayOf(PropTypes.string).isRequired,

  mainAppointmentDraft: PropTypes.shape({
    appointmentType: PropTypes.shape({
      maxAdvance: PropTypes.number.isRequired,
    }).isRequired,
    shop: PropTypes.shape(shopModel).isRequired,
    partner: PropTypes.shape({}).isRequired,
    dateSlot: PropTypes.string,
    timeSlot: PropTypes.shape(timeSlotModel),
    id: PropTypes.string,
  }).isRequired,

  relatedAppointmentTimeSlot: PropTypes.shape(timeSlotModel),
  relatedAppointmentTypeId: PropTypes.string,
  isMultipleAppointment: PropTypes.bool.isRequired,

  temporaryAppointment: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  originalFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  filteredDateSlots: PropTypes.shape(dateSlotModel),
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  isRemoteConfigEnabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitleComponent: PropTypes.func,
  ShopsDropdown: PropTypes.func,
  shopList: PropTypes.arrayOf(PropTypes.shape(shopModel)),
  isApiCallFinished: PropTypes.bool.isRequired,

  config: PropTypes.shape({
    firstEditPagePath: PropTypes.string,
    partnerTitle: PropTypes.string,
    Calendar: PropTypes.shape({
      calendarTimeSlotsIntro: PropTypes.func,
      timeSlotsList: PropTypes.func,
      eligibleSlotsFilter: PropTypes.func,
      formatTimeSlots: PropTypes.func,
    }),
  }).isRequired,
  editMode: PropTypes.bool.isRequired,
  onSaveDataHandler: PropTypes.func.isRequired,
  temporaryJourneyState: PropTypes.shape({}).isRequired,
  journeyState: PropTypes.shape({}).isRequired,
  hideEditDateTitle: PropTypes.bool,
  hideGoBackButton: PropTypes.bool,
  inStoreAppointmentTypeId: PropTypes.string,
  dispatchSelectPartnersGroup: PropTypes.func.isRequired,
  hideFilters: PropTypes.bool,
  ChosenPartner: PropTypes.func,
  GroupsDropdown: PropTypes.func,

  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadInitialSlots: PropTypes.func.isRequired,
  dispatchLoadPartners: PropTypes.func.isRequired,
  dispatchSelectDateSlot: PropTypes.func.isRequired,
  dispatchSetSlots: PropTypes.func.isRequired,

  dispatchSelectTimeSlot: PropTypes.func.isRequired,
  dispatchSetRelatedAppointmentTimeSlot: PropTypes.func.isRequired,

  dispatchSelectPartner: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchLoadPreviousSlots: PropTypes.func.isRequired,
  dispatchLoadNextSlots: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchSetDraftAppointment: PropTypes.func.isRequired,
  dispatchReplaceTimeFilters: PropTypes.func.isRequired,
  dispatchReplaceFilters: PropTypes.func.isRequired,
  dispatchSetJourneyState: PropTypes.func.isRequired,
  dispatchSaveTemporaryJourneyState: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchSetHeadlineBackButtonConfig: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
};

EditDate.defaultProps = {
  filteredDateSlots: {
    date: '',
    times: [],
  },
  isRemoteConfigEnabled: false,
  subtitleComponent: null,
  ShopsDropdown: null,
  shopList: [],
  prevPath: undefined,
  hideEditDateTitle: false,
  hideGoBackButton: false,
  hideFilters: false,
  inStoreAppointmentTypeId: null,
  ChosenPartner: undefined,
  GroupsDropdown: undefined,
  relatedAppointmentTimeSlot: null,
  relatedAppointmentTypeId: null,
};
